import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import WarehouseConsumptionService from '../services/WarehouseConsumptionService';
import { isEmpty } from 'lodash';
import ConsumptionItemService from '../services/ConsumptionItemService';

const initialState = {
  data: [],
  datas: [],
  formError: [],
  pagination: {
    page: 0,
    per_page: 10,
    total: 0,
    totalPages: 0,
  },
  error: null,
  isSuccess: false,
  loading: false,
  submitLoading: false,
  grand_total: 0,
  consumptionItems: [],
  itemsPagination: {
    page: 0,
    per_page: 10,
    total: 0,
    totalPages: 0,
  },
  itemsGrandTotal: 0,
  itemsLoading: false,
};

export const fetchWarehouseConsumptionList = createAsyncThunk(
  'warehouseConsumption/fetchWarehouseConsumptionList',
  async ({ perPage, page, keyword, startDate, endDate, status, warehouse_id, consumption_type_id }) => {
    try {
      const resp = await WarehouseConsumptionService.List({
        perPage,
        page,
        keyword,
        startDate,
        endDate,
        status,
        warehouse_id,
        consumption_type_id,
      });
      return resp;
    } catch (e) {
      return e.response;
    }
  },
);

export const createWarehouseConsumption = createAsyncThunk(
  'warehouseConsumption/createWarehouseConsumption',
  async (data, { rejectWithValue }) => {
    try {
      const resp = await WarehouseConsumptionService.Create(data);
      return resp;
    } catch (error) {
      return rejectWithValue(error.response);
    }
  },
);

export const updateWarehouseConsumption = createAsyncThunk(
  'warehouseConsumption/updateWarehouseConsumption',
  async (payload, { rejectWithValue }) => {
    try {
      const resp = await WarehouseConsumptionService.Put(payload);
      return resp;
    } catch (error) {
      return rejectWithValue(error.response);
    }
  },
);

export const deleteWarehouseConsumption = createAsyncThunk(
  'warehouseConsumption/deleteWarehouseConsumption',
  async (id, { rejectWithValue }) => {
    try {
      const resp = await WarehouseConsumptionService.Delt(id);
      return { resp, id };
    } catch (error) {
      return rejectWithValue(error.response);
    }
  },
);

export const fetchConsumptionItemList = createAsyncThunk(
  'warehouseConsumption/fetchConsumptionItemList',
  async (params) => {
    try {
      const resp = await ConsumptionItemService.List(params);
      return resp;
    } catch (e) {
      return e.response;
    }
  },
);

export const fetchWarehouseConsumptionById = createAsyncThunk(
  'warehouseConsumption/fetchWarehouseConsumptionById',
  async (id, { rejectWithValue }) => {
    try {
      const resp = await WarehouseConsumptionService.Detail(id);
      return resp;
    } catch (error) {
      return rejectWithValue(error.response);
    }
  },
);

const warehouseConsumptionSlice = createSlice({
  name: 'warehouseConsumption',
  initialState: initialState,
  reducers: {
    resetSuccess: (state) => {
      state.isSuccess = false;
    },
    resetFormError: (state) => {
      state.formError = [];
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWarehouseConsumptionList.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchWarehouseConsumptionList.fulfilled, (state, action) => {
        if (action.payload?.status === 200) {
          state.datas = [...action.payload?.data.data?.warehouseConsumptions];
          state.pagination = action.payload?.data.data?.pagination;
          state.grand_total = action.payload?.data.data?.grand_total || 0;
        } else {
          state.error = action.payload?.data.message;
          toast.error(state.error);
        }
        state.loading = false;
      })
      .addCase(fetchWarehouseConsumptionList.rejected, (state) => {
        state.loading = false;
      })
      .addCase(createWarehouseConsumption.pending, (state) => {
        state.submitLoading = true;
        state.formError = [];
        state.isSuccess = false;
      })
      .addCase(createWarehouseConsumption.fulfilled, (state, action) => {
        state.datas.unshift(action.payload.data.data);
        toast.success(action.payload?.data.message);
        state.pagination.total += 1;
        state.submitLoading = false;
        state.isSuccess = true;
      })
      .addCase(createWarehouseConsumption.rejected, (state, action) => {
        state.submitLoading = false;
        state.isSuccess = false;
        const errors = action.payload?.data.errors;
        if (isEmpty(errors)) {
          toast.error(action.payload?.data.message || 'Something went wrong');
        } else {
          state.formError = errors;
        }
      })
      .addCase(updateWarehouseConsumption.pending, (state) => {
        state.submitLoading = true;
        state.formError = [];
        state.isSuccess = false;
      })
      .addCase(updateWarehouseConsumption.fulfilled, (state, action) => {
        const index = state.datas.findIndex((item) => item.id === action.payload.data.data.id);
        if (index !== -1) {
          state.datas[index] = action.payload.data.data;
        }
        toast.success(action.payload?.data.message);
        state.submitLoading = false;
        state.isSuccess = true;
      })
      .addCase(updateWarehouseConsumption.rejected, (state, action) => {
        state.submitLoading = false;
        state.isSuccess = false;
        const errors = action.payload?.data.errors;
        if (isEmpty(errors)) {
          toast.error(action.payload?.data.message || 'Something went wrong');
        } else {
          state.formError = errors;
        }
      })
      .addCase(deleteWarehouseConsumption.pending, (state) => {
        state.submitLoading = true;
      })
      .addCase(deleteWarehouseConsumption.fulfilled, (state, action) => {
        state.datas = state.datas.filter((item) => item.id !== action.payload.id);
        toast.success(action.payload.resp.data.message);
        state.pagination.total -= 1;
        state.submitLoading = false;
      })
      .addCase(deleteWarehouseConsumption.rejected, (state, action) => {
        state.submitLoading = false;
        toast.error(action.payload?.data.message || 'Something went wrong');
      })
      .addCase(fetchWarehouseConsumptionById.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchWarehouseConsumptionById.fulfilled, (state, action) => {
        state.data = action.payload?.data.data;
        state.loading = false;
      })
      .addCase(fetchWarehouseConsumptionById.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchConsumptionItemList.pending, (state) => {
        state.itemsLoading = true;
      })
      .addCase(fetchConsumptionItemList.fulfilled, (state, action) => {
        if (action.payload?.status === 200) {
          state.consumptionItems = [...(action.payload?.data.data?.consumptionItems || [])];
          state.itemsPagination = action.payload?.data.data?.pagination;
          state.itemsGrandTotal = action.payload?.data.data?.grand_total || 0;
        } else {
          toast.error(action.payload?.data?.message || 'Failed to fetch consumption items');
        }
        state.itemsLoading = false;
      })
      .addCase(fetchConsumptionItemList.rejected, (state) => {
        state.itemsLoading = false;
      });
  },
});

export const { resetSuccess, resetFormError } = warehouseConsumptionSlice.actions;

export const warehouseConsumptionSelector = (state) => state?.warehouseConsumption;

export default warehouseConsumptionSlice.reducer;
